import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Cog6ToothIcon, MegaphoneIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import GlobalSearch from '../components/Portal/GlobalSearch';
import DateTimeWidget from '../components/Portal/Widgets/DateTimeWidget';
import CalendarWidget from '../components/Portal/Widgets/CalendarWidget';
import AnnouncementsWidget from '../components/Portal/Widgets/AnnouncementsWidget';
import TasksWidget from '../components/Portal/Widgets/TasksWidget';
import QuickLinksWidget from '../components/Portal/Widgets/QuickLinksWidget';
import CheckInOut from '../components/CheckInOut';

const Portal = () => {
  const { user } = useAuth();
  const [widgetLayout, setWidgetLayout] = useState({
    showDateTime: true,
    showCalendar: true,
    showAnnouncements: true,
    showTasks: true,
    showQuickLinks: true,
    showCheckInOut: true
  });

  const canManageAnnouncements = user?.role === 'admin' || user?.role === 'hr_manager';

  return (
    <div className="min-h-screen theme-bg-secondary">
      {/* Header - Now scrollable, not sticky */}
      <div className="theme-bg-primary border-b theme-border-primary shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-3 sm:py-4 gap-2">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold theme-text-primary">Internal Portal</h1>
              <p className="text-xs sm:text-sm theme-text-secondary mt-0.5">
                Your centralized workspace for productivity
              </p>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Settings - Could open widget customization */}
              <button
                className="p-2 rounded-lg theme-bg-secondary hover:theme-bg-tertiary transition-colors"
                title="Settings"
              >
                <Cog6ToothIcon className="h-4 w-4 sm:h-5 sm:w-5 theme-text-primary" />
              </button>
            </div>
          </div>

          {/* Global Search - More compact on mobile */}
          <div className="pb-3 sm:pb-4">
            <GlobalSearch />
          </div>
        </div>
      </div>

      {/* Main Content - Widget Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Announcements Management Banner for Admin/HR - Compact on mobile */}
        {canManageAnnouncements && (
          <div className="mb-4 sm:mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3 sm:p-4 shadow-sm">
            <div className="flex items-start space-x-3 sm:space-x-4">
              <div className="flex-shrink-0 hidden sm:block">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                  <MegaphoneIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
                  📢 Manage Announcements
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3">
                  Create, edit, and manage announcements. Control who can create announcements and track views.
                </p>
                <Link
                  to="/announcements"
                  className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm font-medium rounded-lg transition-colors shadow-sm"
                >
                  <MegaphoneIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                  <span className="hidden sm:inline">Open Announcements Management</span>
                  <span className="sm:hidden">Manage Announcements</span>
                  <ArrowRightIcon className="h-3 w-3 sm:h-4 sm:w-4 ml-1.5 sm:ml-2" />
                </Link>
                <p className="text-xs text-gray-500 mt-1.5 sm:mt-2 hidden sm:block">
                  💡 Tip: You can also find this in the left sidebar under "Announcements"
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Spans 2 columns on large screens */}
          <div className="lg:col-span-2 space-y-6">
            {/* Announcements Widget */}
            {widgetLayout.showAnnouncements && (
              <div className="w-full">
                <AnnouncementsWidget />
              </div>
            )}

            {/* Tasks Widget */}
            {widgetLayout.showTasks && (
              <div className="w-full">
                <TasksWidget />
              </div>
            )}

            {/* Quick Links Widget */}
            {widgetLayout.showQuickLinks && (
              <div className="w-full">
                <QuickLinksWidget />
              </div>
            )}

            {/* Clock In/Out Widget */}
            {widgetLayout.showCheckInOut && (
              <div className="w-full">
                <div className="portal-widget">
                  <div className="portal-widget-header">
                    <h3 className="portal-widget-title flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Time Clock
                    </h3>
                  </div>
                  <CheckInOut />
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Sidebar Widgets */}
          <div className="space-y-6">
            {/* Date Time Widget */}
            {widgetLayout.showDateTime && (
              <div className="w-full">
                <DateTimeWidget />
              </div>
            )}

            {/* Calendar Widget */}
            {widgetLayout.showCalendar && (
              <div className="w-full">
                <CalendarWidget />
              </div>
            )}
          </div>
        </div>

        {/* Welcome Message / Instructions */}
        <div className="mt-8 portal-widget">
          <div className="text-center py-6">
            <h2 className="text-lg font-semibold theme-text-primary mb-2">
              Welcome to Your Internal Portal
            </h2>
            <p className="theme-text-secondary max-w-2xl mx-auto">
              This is your centralized hub for all workplace activities. Use the search bar above to find employees,
              documents, tasks, and more. Manage your tasks, view announcements, organize your quick links, and stay
              on top of your calendar - all in one place.
            </p>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="text-center p-4 theme-bg-secondary rounded-lg">
              <div className="text-3xl mb-2">📢</div>
              <div className="text-sm theme-text-secondary">Announcements</div>
            </div>
            <div className="text-center p-4 theme-bg-secondary rounded-lg">
              <div className="text-3xl mb-2">✓</div>
              <div className="text-sm theme-text-secondary">Tasks</div>
            </div>
            <div className="text-center p-4 theme-bg-secondary rounded-lg">
              <div className="text-3xl mb-2">📅</div>
              <div className="text-sm theme-text-secondary">Calendar</div>
            </div>
            <div className="text-center p-4 theme-bg-secondary rounded-lg">
              <div className="text-3xl mb-2">🔗</div>
              <div className="text-sm theme-text-secondary">Quick Links</div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center theme-text-tertiary text-sm">
          <p>
            Internal Portal v1.0
          </p>
          <p className="mt-1">
            Your centralized workspace for productivity and collaboration
          </p>
        </div>
      </div>
    </div>
  );
};

export default Portal;
